/**
 * Escritas da página de Cartões pelos repositórios do app: pagar fatura (parcial ou total, atômico,
 * com a transação de liquidação na conta), desfazer o pagamento, e criar/editar/arquivar cartão.
 */
import { and, eq, isNull } from 'drizzle-orm';
import { db } from '@/db/client';
import { transactions } from '@/db/schema';
import { archiveCreditCard, createCreditCard, getCreditCardById, unarchiveCreditCard, updateCreditCard } from '@/db/repositories/creditCardRepository';
import { deleteInvoicePayment, getInvoicePayments, payInvoice, recalculateInvoiceTotal } from '@/db/repositories/invoiceRepository';
import { AdjustmentType } from '@/lib/constants';
import type { CardsEditor } from '../viewer/lib/editing';

/**
 * "Vencimento em dias úteis" mudou (ADR 0230): a fatura prevista ("Fatura <cartão>", na conta que
 * paga) carrega o vencimento ÚTIL. No app quem acerta a data dela é a leitura das faturas
 * (`ensureInvoiceExpenseTransaction`); o Web monta as telas pelo backup e não passa por lá, então
 * recalcula aqui as faturas do cartão que têm uma prevista em aberto — o mesmo caminho que a
 * compra e o pagamento usam.
 */
async function refreshPendingInvoiceExpenses(cardId: string): Promise<void> {
  const rows = await db.selectDistinct({ invoiceId: transactions.invoiceId }).from(transactions).where(and(
    eq(transactions.creditCardId, cardId),
    eq(transactions.adjustmentType, AdjustmentType.INVOICE_EXPENSE),
    eq(transactions.isConfirmed, 0),
    isNull(transactions.deletedAt),
  ));
  for (const { invoiceId } of rows) if (invoiceId) await recalculateInvoiceTotal(invoiceId);
}

export function createCardsEditor(after: () => Promise<void>): CardsEditor {
  return {
    async pay(input) {
      await payInvoice(input.invoiceId, input.accountId, input.amount, input.date);
      const payments = await getInvoicePayments(input.invoiceId);
      const mine = payments.find(p => Math.abs(p.amount - input.amount) < 0.005 && p.date === input.date && p.accountId === input.accountId) ?? payments[0];
      await after();
      if (!mine) throw new Error('Pagamento gravado, mas não encontrei o registro pra poder desfazer.');
      return mine.id;
    },
    async undoPayment(paymentId) { await deleteInvoicePayment(paymentId); await after(); },
    async createCard(input) {
      const c = await createCreditCard({ name: input.name, creditLimit: input.creditLimit, closingDay: input.closingDay, dueDay: input.dueDay, dueOnBusinessDay: input.dueOnBusinessDay ? 1 : 0, paymentAccountId: input.paymentAccountId } as Parameters<typeof createCreditCard>[0]);
      await after();
      return c.id;
    },
    async updateCard(id, input) {
      const before = await getCreditCardById(id);
      await updateCreditCard(id, { name: input.name, creditLimit: input.creditLimit, closingDay: input.closingDay, dueDay: input.dueDay, dueOnBusinessDay: input.dueOnBusinessDay ? 1 : 0, paymentAccountId: input.paymentAccountId } as Parameters<typeof updateCreditCard>[1]);
      if (!!before?.dueOnBusinessDay !== input.dueOnBusinessDay) await refreshPendingInvoiceExpenses(id);
      await after();
    },
    async archiveCard(id) { await archiveCreditCard(id); await after(); },
    async unarchiveCard(id) { await unarchiveCreditCard(id); await after(); },
  };
}
